import React, { useState, useEffect } from 'react';
import { fetchTagDetail } from '../services/api';
import { PlatformBadge, Skeleton } from './common';
import MediaCard from './MediaCard';
import RankExplanation from './RankExplanation';
import './TagDetailView.css';

/**
 * Tag Detail View Component
 * Full tag details with tabs: Overview, Lifecycle, Content Guide, Media, History
 * 
 * @param {Object} props
 * @param {string} props.tagId - Tag ID to display
 * @param {string} props.regionCode - Region code for fetching tag (default: "GLOBAL")
 * @param {Function} props.onBack - Callback to go back
 * @param {Function} props.onMediaClick - Callback when media item is clicked
 */
function TagDetailView({ tagId, regionCode = 'GLOBAL', onBack, onMediaClick }) {
  const [tagData, setTagData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const loadTagDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        // Pass region_code to fetch tag for the specific region user was viewing
        const data = await fetchTagDetail(tagId, { region_code: regionCode });
        setTagData(data);
      } catch (err) {
        setError('Failed to load tag details. Please try again.');
        console.error('Failed to load tag detail:', err);
      } finally {
        setLoading(false);
      }
    };
    loadTagDetail();
  }, [tagId, regionCode]);

  // Format helpers
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  if (loading) {
    return (
      <div className="tag-detail-view">
        <header className="detail-header">
          <button className="back-btn" onClick={onBack}>← Back to Tags</button>
        </header>
        <div className="loading-skeleton">
          <Skeleton height="40px" width="60%" />
          <div className="skeleton-stats">
            <Skeleton height="60px" width="100px" borderRadius={10} />
            <Skeleton height="60px" width="100px" borderRadius={10} />
          </div>
          <Skeleton height="24px" width="200px" />
          <Skeleton height="120px" width="100%" borderRadius={10} />
          <Skeleton height="24px" width="180px" />
          <Skeleton height="80px" width="100%" borderRadius={10} />
        </div>
      </div>
    );
  }

  if (error || !tagData) {
    return (
      <div className="tag-detail-view">
        <div className="error-state">
          <p>{error || 'Tag not found'}</p>
          <button onClick={onBack}>Go Back</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'content-guide', label: 'Content Guide' },
    { id: 'media', label: 'Media' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab tagData={tagData} formatNumber={formatNumber} onMediaClick={onMediaClick} />;
      case 'content-guide':
        return <ContentGuideTab tagData={tagData} onMediaClick={onMediaClick} />;
      case 'media':
        return <MediaTab tagData={tagData} onMediaClick={onMediaClick} />;
      default:
        return null;
    }
  };

  // Get region rankings for display
  const regionRanks = tagData.region_rankings || [];

  return (
    <div className="tag-detail-view">
      {/* Header */}
      <header className="detail-header">
        <button className="back-btn" onClick={onBack}>← Back to Tags</button>
      </header>

      {/* Tag Hero */}
      <div className="tag-hero">
        <div className="tag-hero-main">
          <h1 className="tag-title">
            <span className="hashtag">#</span>{tagData.tag_name}
          </h1>
          {/* Rank badge removed - rank only shown in list view for consistency */}
        </div>
        <div className="tag-hero-stats">
          <div className="hero-stat">
            <span className="hero-stat-value">{tagData.metrics?.trend_count || tagData.trend_count || 0}</span>
            <span className="hero-stat-label">media</span>
          </div>
          {tagData.trending_score && (
            <div className="hero-stat">
              <span className="hero-stat-value">{typeof tagData.trending_score === 'number' ? tagData.trending_score.toFixed(1) : tagData.trending_score}</span>
              <span className="hero-stat-label">score</span>
            </div>
          )}
        </div>
      </div>

      {/* Region Rankings - Where this tag ranks */}
      {regionRanks.length > 0 && (
        <div className="tag-region-ranks">
          <span className="region-ranks-label">Where this ranks</span>
          <span className="region-ranks-list">
            {regionRanks.map((r, idx) => (
              <React.Fragment key={r.region_code || idx}>
                {idx > 0 && ' · '}
                <span className="region-rank-item">
                  #{r.trending_rank} in {r.region_name || r.region_code}
                </span>
              </React.Fragment>
            ))}
          </span>
        </div>
      )}

      {/* Tabs */}
      <nav className="detail-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
}

// ============================================
// Tab Components
// ============================================

function OverviewTab({ tagData, formatNumber, onMediaClick }) {
  // Get platform breakdown data
  const platformBreakdown = tagData.platform_breakdown || {};
  const platforms = Object.entries(platformBreakdown).sort((a, b) => 
    (b[1].contribution || 0) - (a[1].contribution || 0)
  );

  return (
    <div className="overview-tab">
      {/* Why Trending (AI Summary) */}
      {tagData.why_trending_summary && (
        <section className="detail-section">
          <h2>🔥 Why It's Trending</h2>
          <p className="why-text">{tagData.why_trending_summary}</p>
        </section>
      )}

      {/* Platform Breakdown - Visual Bar Chart */}
      {platforms.length > 0 && (
        <section className="detail-section">
          <h2>📊 Platform Breakdown</h2>
          <div className="platform-breakdown-bars">
            {platforms.map(([platform, data]) => {
              const contribution = Math.round((data.contribution || 0) * 100);
              return (
                <div key={platform} className="platform-bar-item">
                  <div className="platform-bar-header">
                    <PlatformBadge platform={platform} size="small" />
                    <span className="platform-bar-pct">{contribution}%</span>
                  </div>
                  <div className="platform-bar-track">
                    <div 
                      className={`platform-bar-fill platform-${platform}`}
                      style={{ width: `${contribution}%` }}
                    />
                  </div>
                  <div className="platform-bar-stats">
                    <span>{data.count || 0} videos</span>
                    <span className="engagement-with-tooltip">
                      {formatNumber(data.total_engagement || 0)} engagement
                      <span className="tooltip-icon" title="Engagement = Views + (Likes × 2) + (Comments × 5) + (Shares × 10)">ℹ️</span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Score Breakdown - shows how the trending score is calculated */}
      {tagData.rank_explanation && Object.keys(tagData.rank_explanation).length > 0 && (
        <section className="detail-section rank-section">
          <h2>📋 Score Breakdown</h2>
          {/* trendingRank={null} to hide rank in detail view */}
          <RankExplanation
            rankExplanation={tagData.rank_explanation}
            trendingScore={tagData.trending_score}
            trendingRank={null}
            expanded={false}
          />
        </section>
      )}

      {/* Platforms: from metrics.platforms (cache) or top-level platforms (BigQuery fallback) */}
      {!tagData.platform_breakdown && ((tagData.platforms || tagData.metrics?.platforms)?.length > 0) && (
        <section className="detail-section">
          <h2>Platforms</h2>
          <div className="platforms-list">
            {(tagData.platforms || tagData.metrics?.platforms || []).map((platform, idx) => (
              <PlatformBadge key={idx} platform={platform} size="medium" />
            ))}
          </div>
        </section>
      )}

      {/* Score Components (fallback if no rank_explanation) */}
      {!tagData.rank_explanation && (tagData.frequency_score || tagData.cross_platform_score) && (
        <section className="detail-section">
          <h2>Score Components</h2>
          <div className="score-components-grid">
            {tagData.avg_top_media_score != null && (
              <div className="score-component-card">
                <span className="component-icon">⭐</span>
                <span className="component-label">Media Quality</span>
                <span className="component-value">{tagData.avg_top_media_score.toFixed(1)}</span>
              </div>
            )}
            {tagData.frequency_score != null && (
              <div className="score-component-card">
                <span className="component-icon">📊</span>
                <span className="component-label">Frequency</span>
                <span className="component-value">{tagData.frequency_score.toFixed(1)}</span>
              </div>
            )}
            {tagData.cross_platform_score != null && (
              <div className="score-component-card">
                <span className="component-icon">🌐</span>
                <span className="component-label">Cross-Platform</span>
                <span className="component-value">{tagData.cross_platform_score.toFixed(1)}</span>
              </div>
            )}
            {tagData.recency_score != null && (
              <div className="score-component-card">
                <span className="component-icon">🕐</span>
                <span className="component-label">Recency</span>
                <span className="component-value">{tagData.recency_score.toFixed(1)}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Top Media Preview */}
      {tagData.linked_media && tagData.linked_media.length > 0 && (
        <section className="detail-section">
          <h2>Top Media</h2>
          <div className="media-preview-grid">
            {tagData.linked_media.slice(0, 6).map((media) => (
              <MediaCard
                key={media.trend_id}
                media={media}
                compact
                onClick={() => onMediaClick && onMediaClick(media.trend_id)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function ContentGuideTab({ tagData, onMediaClick }) {
  const patterns = tagData.content_patterns || {};
  const brandTone = tagData.brand_tone || null;

  return (
    <div className="content-guide-tab">
      {/* Brand Tone */}
      {brandTone && (
        <section className="detail-section">
          <h2>🎯 Brand Tone</h2>
          <div className="brand-tone-card">
            <div className="brand-tone-header">
              <span className="tone-label">{brandTone.detected_tone || 'neutral'}</span>
              {typeof brandTone.tone_confidence === 'number' && (
                <span className="tone-confidence">
                  Confidence: {(brandTone.tone_confidence * 100).toFixed(0)}%
                </span>
              )}
            </div>
            {brandTone.tone_rationale && (
              <p className="tone-rationale">{brandTone.tone_rationale}</p>
            )}
            {brandTone.tone_sources && brandTone.tone_sources.length > 0 && (
              <div className="tone-sources">
                Sources: {brandTone.tone_sources.join(', ')}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Recommended Format: format_type = structure (duration/pacing/presenter); reasoning = insights from top videos */}
      {patterns.recommended_format && (
        <section className="detail-section">
          <h2>📐 Recommended Format</h2>
          <div className="format-card">
            <span className="format-type">{patterns.recommended_format.format_type?.replace(/_/g, ' ')}</span>
            {patterns.recommended_format.reasoning && (
              <>
                <p className="format-reasoning-label">Insights from top-performing videos</p>
                <p className="format-reasoning">{patterns.recommended_format.reasoning}</p>
              </>
            )}
            {patterns.recommended_format.based_on_media && patterns.recommended_format.based_on_media.length > 0 && (
              <p className="format-based-on">
                Based on {patterns.recommended_format.based_on_media.length} video{patterns.recommended_format.based_on_media.length !== 1 ? 's' : ''}:
                {' '}
                {patterns.recommended_format.based_on_media.map((m, i) => (
                  <span key={m.trend_id || i}>
                    {i > 0 && ', '}
                    <button
                      type="button"
                      className="format-source-link"
                      onClick={() => onMediaClick && onMediaClick(m.trend_id)}
                      title={m.title}
                    >
                      {m.title && m.title.length > 50 ? `${m.title.slice(0, 50)}…` : m.title || 'Video'}
                    </button>
                  </span>
                ))}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Winning Hooks */}
      {patterns.winning_hooks && patterns.winning_hooks.length > 0 && (
        <section className="detail-section">
          <h2>🎣 Winning Hooks</h2>
          <div className="hooks-list">
            {patterns.winning_hooks.map((hook, idx) => (
              <div key={idx} className="hook-card">
                <span className="hook-type">{hook.hook_type?.replace(/-/g, ' ')}</span>
                <p className="hook-example">"{hook.example_title}"</p>
              </div>
            ))}
          </div>
        </section>
      )}

    </div>
  );
}

function MediaTab({ tagData, onMediaClick }) {
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all');

  const allMedia = tagData.linked_media || [];
  const totalCount = tagData.trend_count ?? tagData.metrics?.trend_count ?? allMedia.length;
  const isCapped = totalCount > allMedia.length;
  // linked_media_by_type is currently not rendered here; kept for future UI extension.

  // Get unique media types
  const mediaTypes = ['all', ...new Set(allMedia.map(m => m.media_type))];

  // Filter media
  const filteredMedia = mediaTypeFilter === 'all' 
    ? allMedia 
    : allMedia.filter(m => m.media_type === mediaTypeFilter);

  return (
    <div className="media-tab">
      {isCapped && (
        <p className="media-tab-capped-note">
          Showing top {allMedia.length} of {totalCount} media (by trending score).
        </p>
      )}
      {/* Media Type Filter */}
      <div className="media-type-filter">
        {mediaTypes.map(type => (
          <button
            key={type}
            className={`filter-btn ${mediaTypeFilter === type ? 'active' : ''}`}
            onClick={() => setMediaTypeFilter(type)}
          >
            {type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>

      {/* Media Grid */}
      <div className="media-grid">
        {filteredMedia.map((media) => (
          <MediaCard 
            key={media.trend_id} 
            media={media} 
            onClick={() => onMediaClick && onMediaClick(media.trend_id)}
          />
        ))}
      </div>

      {filteredMedia.length === 0 && (
        <div className="empty-media">
          <p>No media found for this filter.</p>
        </div>
      )}
    </div>
  );
}

export default TagDetailView;
